/**
 * Simple language detection for Vietnamese vs English
 * Uses character-based heuristics instead of heavy ML libraries
 */

// Vietnamese special characters
const VIETNAMESE_CHARS = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i;

/**
 * Detect if text is Vietnamese or English
 * @param text - Text to analyze
 * @returns 'vi' if Vietnamese detected, 'en' otherwise
 */
export const detectLanguage = (text: string): 'vi' | 'en' => {
  if (!text || text.length < 5) {
    // Too short to detect, use stored preference
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('chatLanguage') as 'vi' | 'en') || 'vi';
    }
    return 'vi';
  }

  // Check for Vietnamese special characters
  if (VIETNAMESE_CHARS.test(text)) {
    return 'vi';
  }

  // Count Vietnamese words (common words that are unique to Vietnamese)
  const vietnameseWords = [
    'không', 'có', 'được', 'là', 'của', 'và', 'với', 'cho', 'tôi', 'bạn',
    'này', 'như', 'các', 'trong', 'khi', 'đã', 'sẽ', 'thì', 'để', 'những'
  ];

  const lowercaseText = text.toLowerCase();
  const viWordCount = vietnameseWords.filter(word =>
    lowercaseText.includes(word)
  ).length;

  // If found 2+ Vietnamese words, likely Vietnamese
  if (viWordCount >= 2) {
    return 'vi';
  }

  // Default to stored preference or Vietnamese
  if (typeof window !== 'undefined') {
    return (localStorage.getItem('chatLanguage') as 'vi' | 'en') || 'vi';
  }

  return 'vi';
};

/**
 * Save language preference to localStorage
 */
export const saveLanguagePreference = (language: 'vi' | 'en'): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('chatLanguage', language);
  }
};

/**
 * Get saved language preference
 */
export const getLanguagePreference = (): 'vi' | 'en' => {
  if (typeof window !== 'undefined') {
    return (localStorage.getItem('chatLanguage') as 'vi' | 'en') || 'vi';
  }
  return 'vi';
};
